// FundDetailModal — the fund fact-sheet overlay opened from any fund row / chart
// segment. Generic, presentational (fund + detail objects in, callbacks out): one
// content body rendered inside the reusable Modal on desktop and the BottomSheet
// on mobile (both handle focus-trap / Esc / scrim / scroll-lock). Key stats,
// performance vs category & benchmark, asset mix and top holdings. Shared across
// screens (My Portfolio and the Dashboard holdings glimpse). Source of truth:
// portfolio3.jsx FundDetailModal.
//
// DOCUMENTS. Which documents a fund publishes varies by fund and by plan, so the
// tab strip is data-driven: `detail.documents` lists what this fund has in this
// plan, and only those tabs render. A fund with one document gets no tab strip at
// all — a lone tab is a control that can't do anything. Omit `documents` and both
// are assumed, which is what the fallback plans without fund detail rely on.
// (Summary Prospectus was dropped: it duplicated the full prospectus.)
//
// THE SHEET ITSELF. When the caller supplies `sheetUrl` (the API's inline PDF), the Fact
// Sheet tab shows the real published document in a viewer rather than the summary
// fields — the participant sees the sheet, not a download. The fallback inside the
// viewer covers a fund the brand API doesn't know. Without `sheetUrl` the tab renders
// the structured summary from `detail` exactly as before.
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FUND_MIX, colorAt } from '../../../utils/chartColors';
import { Modal } from '../dialog/Modal';
import { BottomSheet } from '../dialog/BottomSheet';
import { Tabs } from '../navigation/Tabs';
import { SegBar } from '../charts/SegBar';
import { Spinner } from '../feedback/Spinner';
import { useMediaQuery } from '../../../hooks';
import { cx } from '../../../utils/cx';
import { formatPercent, formatDate } from '../../../utils/format';
import { CloseIcon, DownloadIcon, ExternalIcon, InfoIcon } from '../../icons/Icons';
import './fund-detail-modal.css';

// Period codes double as data keys (detail.compare[p]); display labels reuse the
// shared portfolio.perf.p_* translations.
const PERIODS = ['1Yr', '3Yr', '5Yr', '10Yr'];
const PERIOD_LABEL_KEY = { '1Yr': 'p_oneYr', '3Yr': 'p_threeYr', '5Yr': 'p_fiveYr', '10Yr': 'p_tenYr' };
// Mix-chart slice colours come from `FUND_MIX` in utils/chartColors, the one owner of
// by-position palettes (five slices max — this chart never shows more).

// The documents a fund can publish, in display order. Ids are the wire values in
// `detail.documents`; labels resolve through i18n.
const DOC_IDS = ['fact', 'pros'];
const DOC_LABEL_KEY = {
  fact: 'portfolio.fundDetail.tabFact',
  pros: 'portfolio.fundDetail.tabProspectus',
};

const pct2 = (v) => formatPercent(v, { digits: 2, signed: true });
// Tone of a category/benchmark cell relative to the fund: green = fund beats it.
const compareTone = (fund, other) => (fund > other ? 'pos' : fund < other ? 'neg' : '');

// PDF open parameters (Chrome/Edge/pdf.js): no viewer toolbar or side panel — the sheet's
// own footer carries the one action — and fit the page to the viewer's width so it reads
// on a phone. "Open in new tab" uses the bare URL and keeps the full viewer.
const VIEWER_PARAMS = '#toolbar=0&navpanes=0&view=FitH';

/**
 * Resolve the URL the viewer should render, and whether it can:
 *   'ok'       → render `viewUrl`
 *   'down'     → the brand API is failing (our API says 503): "temporarily unavailable" + retry
 *   'missing'  → no sheet is published for this fund (404)
 *
 * `sourceUrl` is the brand API's own link — the one Download uses. It answers
 * `Content-Disposition: attachment`, so a viewer pointed straight at it only downloads,
 * but it also allows cross-origin reads, so the bytes are fetched here in the browser
 * and rendered from a `blob:` URL, which carries no disposition. That keeps the viewer
 * on the same network path as Download rather than on the API's server-side hop.
 *
 * `apiUrl` (our API's inline re-serve) is the fallback: probed with a HEAD so the
 * participant reads a sentence about what happened rather than a raw problem-JSON page.
 * Where `fetch` is absent (jsdom) or the probe itself fails (CORS on a cross-origin dev
 * API), fall through to 'ok' and let the `object` element's own fallback cover it.
 */
function useSheet(sourceUrl, apiUrl) {
  const [state, setState] = useState({ status: 'checking', viewUrl: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!sourceUrl && !apiUrl) return undefined;
    if (typeof fetch !== 'function') { setState({ status: 'ok', viewUrl: apiUrl }); return undefined; }
    let alive = true;
    let objectUrl = null;
    setState({ status: 'checking', viewUrl: null });

    const probeApi = () => {
      if (!apiUrl) { if (alive) setState({ status: 'missing', viewUrl: null }); return; }
      fetch(apiUrl, { method: 'HEAD', credentials: 'same-origin' })
        .then((r) => {
          if (!alive) return;
          // 405 = a backend that doesn't answer HEAD yet: not a verdict on the sheet, so
          // render and let the viewer's own fallback speak if the GET then fails.
          if (r.ok || r.status === 405) setState({ status: 'ok', viewUrl: apiUrl });
          else if (r.status === 404) setState({ status: 'missing', viewUrl: null });
          else setState({ status: 'down', viewUrl: null });
        })
        .catch(() => { if (alive) setState({ status: 'ok', viewUrl: apiUrl }); });
    };

    if (!sourceUrl || typeof URL?.createObjectURL !== 'function') {
      probeApi();
    } else {
      fetch(sourceUrl)
        .then(async (r) => {
          // An HTML error page for an unknown ticker is a 200 too; only a PDF counts.
          if (!r.ok || !(r.headers.get('content-type') || '').toLowerCase().includes('pdf')) throw new Error('not a pdf');
          const blob = await r.blob();
          if (!alive) return;
          objectUrl = URL.createObjectURL(blob);
          setState({ status: 'ok', viewUrl: objectUrl });
        })
        .catch(() => { if (alive) probeApi(); });
    }
    // Revoke the blob once this sheet is superseded or closed, after a grace period so
    // an "Open in new tab" that is still loading keeps its source.
    return () => {
      alive = false;
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    };
  }, [sourceUrl, apiUrl, attempt]);
  return { ...state, retry: () => setAttempt((n) => n + 1) };
}

function SheetViewer({ url, t }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      {/* Sits BEHIND the object: the PDF plugin paints opaque over it on first render, so
          the spinner clears even where `load` never fires on an <object>. */}
      {!loaded && (
        <div className="fund-detail__viewer-loading">
          <Spinner size="lg" label={t('portfolio.fundDetail.sheetChecking')} />
        </div>
      )}
      {/* `object` (not iframe): if the response still isn't a PDF, the browser renders the
          fallback children instead of a JSON page. */}
      <object
        className="fund-detail__viewer"
        data={`${url}${VIEWER_PARAMS}`}
        type="application/pdf"
        aria-label={t('portfolio.fundDetail.tabFact')}
        onLoad={() => setLoaded(true)}
      >
        <div className="fund-detail__doc-empty">
          <p>{t('portfolio.fundDetail.viewerUnavailable')}</p>
        </div>
      </object>
    </>
  );
}
SheetViewer.propTypes = { url: PropTypes.string.isRequired, t: PropTypes.func.isRequired };

// A one-line strip above the fund summary when the published sheet can't be shown: what
// happened, in plain words, and (when it may come back) a Try again link. The summary
// the app already has stays on screen underneath — the participant is never left with
// an empty panel.
function SheetNotice({ status, onRetry, t }) {
  if (status === 'ok' || status === 'checking') return null;
  const key = status === 'missing' ? 'sheetMissing' : 'sheetDown';
  return (
    <div className="fund-detail__notice" role="status">
      <InfoIcon width={14} height={14} aria-hidden="true" />
      <span>{t(`portfolio.fundDetail.${key}`)}</span>
      {status === 'down' && (
        <button type="button" className="fund-detail__notice-retry" onClick={onRetry}>
          {t('portfolio.fundDetail.retry')}
        </button>
      )}
    </div>
  );
}
SheetNotice.propTypes = {
  status: PropTypes.oneOf(['checking', 'ok', 'down', 'missing']).isRequired,
  // 'checking' is accepted but renders nothing: the body carries the spinner.
  onRetry: PropTypes.func,
  t: PropTypes.func.isRequired,
};

function FundDetailContent({ fund, detail, asOf, sheetUrl, sheetSourceUrl, onClose, onDownload, onOpenExternal }) {
  const { t } = useTranslation();
  const hasSheet = !!(sheetUrl || sheetSourceUrl);
  const sheet = useSheet(sheetSourceUrl, sheetUrl);
  // The documents this fund publishes, in display order. An unrecognised id is
  // dropped rather than rendered as an empty tab; nothing recognised falls back to
  // both, so a payload that omits the field behaves as it always did.
  const docIds = (detail?.documents || DOC_IDS).filter((d) => DOC_IDS.includes(d));
  const docs = docIds.length ? docIds : DOC_IDS;
  const [docTab, setDocTab] = useState(docs[0]);
  // A fund whose document set doesn't include the selected tab (the overlay is
  // reused for the next fund) shows its first document instead of a blank body.
  const activeDoc = docs.includes(docTab) ? docTab : docs[0];
  const docTabs = docs.map((id) => ({ id, label: t(DOC_LABEL_KEY[id]) }));
  // The PDF shows only once the API has said it is there; otherwise the summary shows,
  // with a notice strip saying why, and the footer offers the PDF as a download instead.
  const showSheet = hasSheet && sheet.status === 'ok';
  // Until the probe answers, a spinner: showing the summary first and then swapping it
  // for the PDF read as a flash of the wrong content.
  const checking = hasSheet && sheet.status === 'checking';

  return (
    <div className={cx('fund-detail', showSheet && 'fund-detail--sheet')}>
      <header className="fund-detail__head">
        <div className="fund-detail__id">
          <div className="fund-detail__tag">
            <span className="fund-detail__sw" style={{ background: fund.color }} />
            <span className="fund-detail__tkr">{fund.ticker}</span>
            {detail?.category && <span className="fund-detail__cat">{detail.category}</span>}
          </div>
          <h2 className="fund-detail__title">{fund.name}</h2>
          <div className="fund-detail__sub">
            {fund.family}
            {detail?.benchmark && (
              <>
                {' · '}{t('portfolio.fundDetail.benchmark')} <strong>{detail.benchmark}</strong>
              </>
            )}
          </div>
        </div>
        <button type="button" className="fund-detail__close" onClick={onClose} aria-label={t('portfolio.fundDetail.close')}>
          <CloseIcon />
        </button>
      </header>

      {hasSheet && !showSheet && <SheetNotice status={sheet.status} onRetry={sheet.retry} t={t} />}

      {checking ? (
        <div className="fund-detail__body fund-detail__body--sheet fund-detail__body--loading">
          <Spinner size="lg" label={t('portfolio.fundDetail.sheetChecking')} />
        </div>
      ) : showSheet ? (
        <div className="fund-detail__body fund-detail__body--sheet">
          <SheetViewer url={sheet.viewUrl} t={t} />
        </div>
      ) : detail ? (
        <>
          {/* One document → no tab strip; there'd be nothing to switch to. */}
          {docs.length > 1 && (
            <Tabs tabs={docTabs} value={activeDoc} onChange={setDocTab} className="fund-detail__tabs" />
          )}

          <div className="fund-detail__body">
            {activeDoc === 'fact' && (
              <>
                <section className="fund-detail__section">
                  <div className="fund-detail__eyebrow">{t('portfolio.fundDetail.objective')}</div>
                  <p className="fund-detail__obj">{detail.objective}</p>
                </section>

                <section className="fund-detail__section">
                  <div className="fund-detail__eyebrow">{t('portfolio.fundDetail.keyStats')}</div>
                  <div className="fund-detail__stats">
                    <div>
                      <span className="fund-detail__stat-lbl">{t('portfolio.fundDetail.totalAssets')}</span>
                      <span className="fund-detail__stat-val">{detail.assets}</span>
                    </div>
                    <div>
                      <span className="fund-detail__stat-lbl">{t('portfolio.fundDetail.inception')}</span>
                      <span className="fund-detail__stat-val">{detail.inception}</span>
                    </div>
                    <div>
                      <span className="fund-detail__stat-lbl">{t('portfolio.fundDetail.turnover')}</span>
                      <span className="fund-detail__stat-val">{detail.turnover}</span>
                    </div>
                    <div>
                      <span className="fund-detail__stat-lbl">{t('portfolio.fundDetail.expenseRatio')}</span>
                      <span className="fund-detail__stat-val">
                        {fund.expense != null ? `${fund.expense.toFixed(2)}%` : '—'}{' '}
                        <span className="fund-detail__stat-muted">{t('portfolio.fundDetail.costPer', { cost: detail.costPer1000 })}</span>
                      </span>
                    </div>
                    <div>
                      <span className="fund-detail__stat-lbl">{t('portfolio.fundDetail.fee12b1')}</span>
                      <span className="fund-detail__stat-val">{detail.fee12b1}</span>
                    </div>
                    <div>
                      <span className="fund-detail__stat-lbl">{t('portfolio.fundDetail.salesCharge')}</span>
                      <span className="fund-detail__stat-val">{detail.salesCharge}</span>
                    </div>
                  </div>
                </section>

                <section className="fund-detail__section">
                  <div className="fund-detail__eyebrow">{t('portfolio.fundDetail.perfTitle')}</div>
                  <div className="fund-detail__perf">
                    <div className="fund-detail__perf-row fund-detail__perf-head">
                      <span />
                      <span>{t('portfolio.fundDetail.colFund')}</span>
                      <span>{t('portfolio.fundDetail.colCategory')}</span>
                      <span>{t('portfolio.fundDetail.colBenchmark')}</span>
                    </div>
                    {PERIODS.map((p) => {
                      const r = detail.compare?.[p];
                      if (!r) return null;
                      return (
                        <div key={p} className="fund-detail__perf-row">
                          <span className="fund-detail__perf-period">{t(`portfolio.perf.${PERIOD_LABEL_KEY[p]}`)}</span>
                          <span className={cx('fund-detail__perf-val', r.fund >= 0 ? 'is-pos' : 'is-neg')}>
                            {pct2(r.fund)}
                          </span>
                          <span className={cx('fund-detail__perf-val', `is-${compareTone(r.fund, r.cat) || 'flat'}`)}>
                            {pct2(r.cat)}
                          </span>
                          <span className={cx('fund-detail__perf-val', `is-${compareTone(r.fund, r.ben) || 'flat'}`)}>
                            {pct2(r.ben)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="fund-detail__perfnote">
                    {t('portfolio.fundDetail.perfNote')}
                    {asOf && <> {t('portfolio.fundDetail.perfNoteAsOf', { date: formatDate(asOf) })}</>}
                  </div>
                </section>

                <section className="fund-detail__section fund-detail__split">
                  <div>
                    <div className="fund-detail__eyebrow">{t('portfolio.fundDetail.assetMix')}</div>
                    <SegBar
                      className="fund-detail__mix-bar"
                      height={10}
                      segments={(detail.mix || []).map((m, i) => ({
                        value: m.pct,
                        color: colorAt(FUND_MIX, i),
                        label: m.label,
                        tooltip: `${m.label} · ${m.pct}%`,
                      }))}
                    />
                    <div className="fund-detail__mix-legend">
                      {(detail.mix || []).map((m, i) => (
                        <div key={m.label}>
                          <span className="fund-detail__mix-sw" style={{ background: colorAt(FUND_MIX, i) }} />
                          <span className="fund-detail__mix-nm">{m.label}</span>
                          <span className="fund-detail__mix-pct">{m.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="fund-detail__eyebrow">{t('portfolio.fundDetail.topHoldings')}</div>
                    <div className="fund-detail__holdings">
                      {(detail.holdings || []).map((h, i) => (
                        <div key={h.name} className="fund-detail__holding">
                          <span className="fund-detail__rank">{i + 1}</span>
                          <span className="fund-detail__holding-nm">{h.name}</span>
                          <span className="fund-detail__holding-bar">
                            <span
                              style={{
                                width: `${(h.pct / (detail.holdings[0]?.pct || h.pct)) * 100}%`,
                                background: fund.color,
                              }}
                            />
                          </span>
                          <span className="fund-detail__holding-pct">{h.pct.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              </>
            )}

            {activeDoc === 'pros' && (
              <div className="fund-detail__doc-empty">
                <div className="fund-detail__eyebrow">{t('portfolio.fundDetail.tabProspectus')}</div>
                <p>{t('portfolio.fundDetail.prospectusBody')}</p>
                <p className="fund-detail__muted">{t('portfolio.fundDetail.prospectusNote')}</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="fund-detail__body">
          <div className="fund-detail__doc-empty">
            {/* Only the facts this caller supplied: the Portfolio index has all three, the
                sponsor index has no asset class and sometimes no expense ratio. */}
            <p>
              {[
                fund.assetClass,
                fund.pct != null && t('portfolio.fundDetail.fallbackPct', { pct: Math.round(fund.pct) }),
                fund.expense != null && t('portfolio.fundDetail.fallbackExpense', { expense: fund.expense.toFixed(2) }),
              ].filter(Boolean).join(' · ')}
            </p>
            <p className="fund-detail__muted">{t('portfolio.fundDetail.fallbackNote')}</p>
          </div>
        </div>
      )}

      <footer className="fund-detail__foot">
        {/* Per the design: Download PDF leads, Open in new tab beside it, whenever the
            sheet is known to be there or no inline sheet was offered at all. */}
        {!checking && onDownload && (
          <button type="button" className="fund-detail__btn fund-detail__btn--primary" onClick={onDownload}>
            <DownloadIcon width={14} height={14} /> {t('portfolio.fundDetail.downloadPdf')}
          </button>
        )}
        {!checking && (showSheet || !hasSheet) && onOpenExternal && (
          // A new tab gets the stable sheet URL, never the blob: a blob URL dies with the
          // tab that created it. Our inline re-serve first; the brand link only downloads.
          <button type="button" className="fund-detail__btn fund-detail__btn--ghost" onClick={() => onOpenExternal((sheet.viewUrl || '').startsWith('blob:') ? (sheetUrl || sheetSourceUrl) : (sheet.viewUrl || undefined))}>
            <ExternalIcon /> {t('portfolio.fundDetail.openNewTab')}
          </button>
        )}
        <span className="fund-detail__spacer" />
        {asOf && <span className="fund-detail__asof">{t('portfolio.fundDetail.asOf', { date: formatDate(asOf) })}</span>}
      </footer>
    </div>
  );
}

FundDetailContent.propTypes = {
  fund: PropTypes.object.isRequired,
  detail: PropTypes.object,
  asOf: PropTypes.instanceOf(Date),
  sheetUrl: PropTypes.string,
  sheetSourceUrl: PropTypes.string,
  onClose: PropTypes.func,
  onDownload: PropTypes.func,
  onOpenExternal: PropTypes.func,
};

export function FundDetailModal({ open, fund, detail, asOf, sheetUrl, sheetSourceUrl, onClose, onDownload, onOpenExternal }) {
  // The modal becomes a full-screen bottom sheet at ≤600px (design parity: the
  // centered modal persists down to 601px). Above that it stays a centered Modal.
  const mobile = useMediaQuery('(max-width: 600px)');
  const content =
    open && fund ? (
      <FundDetailContent
        fund={fund}
        detail={detail}
        asOf={asOf}
        sheetUrl={sheetUrl}
        sheetSourceUrl={sheetSourceUrl}
        onClose={onClose}
        onDownload={onDownload}
        onOpenExternal={onOpenExternal}
      />
    ) : null;

  if (mobile) {
    return (
      <BottomSheet open={open} onClose={onClose} flush>
        {content}
      </BottomSheet>
    );
  }
  return (
    <Modal open={open} onClose={onClose} size={sheetUrl || sheetSourceUrl ? 'xl' : 'lg'} flush>
      {content}
    </Modal>
  );
}

FundDetailModal.propTypes = {
  open: PropTypes.bool,
  fund: PropTypes.object,
  detail: PropTypes.object,
  asOf: PropTypes.instanceOf(Date),
  /** Same-origin inline PDF of the published fact sheet (our API); the viewer's fallback. */
  sheetUrl: PropTypes.string,
  /** The brand API's own sheet link; fetched in the browser and rendered from a blob first. */
  sheetSourceUrl: PropTypes.string,
  onClose: PropTypes.func,
  onDownload: PropTypes.func,
  /** Receives the URL the viewer rendered (may be a blob: URL); undefined when none. */
  onOpenExternal: PropTypes.func,
};

export default FundDetailModal;
